import { createContext, FC, ReactNode, useContext, useMemo } from 'react';

import {
	BookingStateEnum,
	PassengerBookingView,
	TripTypeEnum,
	VehicleType,
} from '@mopla/data-models';
import {
	EItineraryTimeChange,
	ERideCanceller,
	ERideStatus,
	ERideType,
	getItineraryRideType,
	getItineraryTimingV2,
	getRideCanceller,
	getRideStatus,
	TItineraryTimeChange,
	TItineraryTiming,
} from '@mopla/utils';

import { getPRODTLeg, getRideTypeIconMode } from '../utils';

interface IBookingRideContextValue {
	booking: PassengerBookingView;
	rideType: ERideType;
	/** From getRideTypeIconMode; fixed for the booking's lifetime. */
	rideIconMode: TripTypeEnum | 'MIXED';
	/** Vehicle class, for MyWay rides only. */
	vehicleType?: VehicleType;
	rideTiming: TItineraryTiming | null;
	rideStatus: ERideStatus | null;
	rideTimeChange: TItineraryTimeChange;
	/** Who cancelled the ride; null while it is not cancelled. */
	rideCanceller: ERideCanceller | null;
}

const BookingRideContext = createContext<IBookingRideContextValue | null>(null);

interface IProviderProps {
	booking: PassengerBookingView;
	children: ReactNode;
}

const NO_TIME_CHANGE: TItineraryTimeChange = {
	type: EItineraryTimeChange.NONE,
	isDepartureChanged: false,
	diffMinutes: 0,
};

/** Everything both the activity card and the booked-ride detail derive from one booking. */
export const BookingRideProvider: FC<IProviderProps> = ({
	booking,
	children,
}) => {
	const rideType = useMemo(
		() => getItineraryRideType(booking.itinerary),
		[booking.itinerary]
	);
	const rideIconMode = useMemo(() => getRideTypeIconMode(rideType), [rideType]);
	const vehicleType = useMemo(
		() => getPRODTLeg(booking.itinerary)?.vehicleType ?? undefined,
		[booking.itinerary]
	);
	const rideTiming = useMemo(
		() => getItineraryTimingV2(booking.itinerary.legs),
		[booking.itinerary]
	);
	const rideStatus = useMemo(() => getRideStatus(booking), [booking]);
	const rideCanceller = useMemo(() => getRideCanceller(booking), [booking]);
	const rideTimeChange = useMemo(() => {
		/**
		 * Delay/time-change info is only relevant while the driver is still
		 * heading to the pickup (booking NEW). Once the ride is executing,
		 * finished or cancelled it must not be shown anymore.
		 */
		if (booking.bookingStatus !== BookingStateEnum.NEW) {
			return NO_TIME_CHANGE;
		}
		return rideTiming?.timeChange ?? NO_TIME_CHANGE;
	}, [booking.bookingStatus, rideTiming]);

	const value = useMemo<IBookingRideContextValue>(
		() => ({
			booking,
			rideType,
			rideIconMode,
			vehicleType,
			rideTiming,
			rideStatus,
			rideTimeChange,
			rideCanceller,
		}),
		[
			booking,
			rideType,
			rideIconMode,
			vehicleType,
			rideTiming,
			rideStatus,
			rideTimeChange,
			rideCanceller,
		]
	);

	return (
		<BookingRideContext.Provider value={value}>
			{children}
		</BookingRideContext.Provider>
	);
};

export const useBookingRide = (): IBookingRideContextValue => {
	const context = useContext(BookingRideContext);

	if (!context) {
		throw new Error('useBookingRide must be used within BookingRideProvider');
	}

	return context;
};
